import { encodeURI } from 'js-base64';
import axiosPortalInstance from './axiosPortalInstance';
import { IJwt, ITokenData, UserInfo } from '../../models/portal/auth.model';
import axiosDmsInstance from '../dms/axiosDmsInstance';
import { encodeInvalidCharacters, handleApiError } from '../../common/util';
import { ITechnician } from '../../models/dms/technician.model';
import { fetchAppConfig } from '../../common/configUtil';
import { IOtpQrCode } from '../../models/portal/otp.model';

export const postLogin = async (username: string, password: string, databaseId: string, otp?: string) => {
  try {
    const url = `/auth/authorize`;

    const response = await axiosPortalInstance.post<IJwt>(url, {
      userName: username,
      password: password,
      databaseId: databaseId,
      deploymentTypeCode: (await fetchAppConfig()).deploymentTypeCode || 'UNKNOWN_DEPLOYMENTTYPECODE',
      moduleCode: process.env.REACT_APP_MODULE_CODE,
      totpcode: otp || '',
    });
    return response.data;
  } catch (err: any) {
    return handleApiError('postLogin', err);
  }
};

export const postRefresh = async (refreshToken: string, databaseId: string) => {
  try {
    const url = `/auth/authorize`;

    const response = await axiosPortalInstance.post<IJwt>(url, {
      grantType: 'refresh_token',
      refreshToken: refreshToken,
      databaseId: databaseId,
      moduleCode: process.env.REACT_APP_MODULE_CODE,
    });
    return response.data;
  } catch (err: any) {
    return handleApiError('postRefresh', err);
  }
};

export const getUserInfo = async (username: string) => {
  try {
    const url = `/api/users/getInfo/${username}`;

    const response = await axiosDmsInstance.get<UserInfo>(url);
    return response.data;
  } catch (err: any) {
    return handleApiError('getUserInfo', err);
  }
};

export const getExpiredToken = (jwt: IJwt) => {
  // Generate token
  let jwtParts = jwt.access_token.split('.');
  let header = jwtParts[0];
  let payload = jwtParts[1];
  let signature = jwtParts[2];

  const payloadDecoded = JSON.parse(atob(payload)) as ITokenData;
  payloadDecoded.exp = 1;
  payload = encodeURI(JSON.stringify(payloadDecoded));

  const token = `${header}.${payload}.${signature}`;
  return token;
};

export const getFakeAccessToken = (dbId: string) => {
  // Generate token
  const encodedTokenHeader = encodeURI(
    JSON.stringify({
      typ: 'JWT',
      alg: 'RS256',
    }),
  );
  const encodedTokenData = encodeURI(
    JSON.stringify({
      dbid: dbId,
      exp: 1,
    }),
  );
  const token = `${encodedTokenHeader}.${encodedTokenData}.`;
  return token;
};

export const getPinIn = async (pin: string) => {
  try {
    const url = `/api/common/technician/v1/validate/${encodeInvalidCharacters(pin)}`;

    const response = await axiosDmsInstance.get<ITechnician>(url);
    return response.data;
  } catch (err: any) {
    return handleApiError('getPinIn', err);
  }
};

export const generateOtpQrCode = async (username: string, password: string, databaseId: string) => {
  try {
    const url = `/auth/user/mfa-setup`;
    const response = await axiosPortalInstance.post<IOtpQrCode>(url, {
      userName: username,
      password: password,
      databaseId: databaseId,
      deploymentTypeCode: (await fetchAppConfig()).deploymentTypeCode || 'UNKNOWN_DEPLOYMENTTYPECODE',
    });
    return {
      key: response.data.key,
      qrCode: `data:image/png;base64,${response.data.qrCode}`,
    } as IOtpQrCode;
  } catch (err: any) {
    return handleApiError('generateOtpQrCode', err);
  }
};

export const registerOtp = async (username: string, password: string, databaseId: string, otp: string) => {
  try {
    const url = `/auth/user/mfa-setup-validate`;
    const response = await axiosPortalInstance.post<void>(url, {
      userName: username,
      password: password,
      databaseId: databaseId,
      deploymentTypeCode: (await fetchAppConfig()).deploymentTypeCode || 'UNKNOWN_DEPLOYMENTTYPECODE',
      totpcode: otp,
    });
    return response.data;
  } catch (err: any) {
    return handleApiError('registerOtp', err);
  }
};
